import { useEffect, useState } from 'react';
import { hasOverride, pageInfo, allPages, removeBlock, type ProjectDoc } from '@cal/engine';
import { get, errorMessage } from '../api';
import { navigate } from '../router';
import { Modal, Spinner } from '../ui/controls';
import { toast } from '../ui/toast';
import { useLibrary } from '../library';
import { clearRecovery, edit, flushPendingSave, loadProject, overwriteAfterConflict, readRecovery, redo, save, undo, useEditor, type ProjectSummary, type Stage } from './store';
import { useAllLayouts, useDocResources } from './hooks';
import { Canvas } from './Canvas';
import { Strip } from './Strip';
import { Inspector } from './Inspector';
import { ProductStage } from './ProductStage';
import { DesignStage } from './DesignStage';
import { ContentStage } from './ContentStage';
import { ReviewStage } from './ReviewStage';

const STAGES: { n: Stage; label: string }[] = [
  { n: 1, label: 'Product' },
  { n: 2, label: 'Design' },
  { n: 3, label: 'Content' },
  { n: 4, label: 'Review & export' },
];

export function EditorPage({ id }: { id: string }) {
  const doc = useEditor((s) => s.doc);
  const project = useEditor((s) => s.project);
  const [error, setError] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<{ at: string; doc: ProjectDoc; baseVersion: number } | null>(null);
  const loadFonts = useLibrary((s) => s.loadFonts);

  useEffect(() => {
    let cancelled = false;
    useEditor.setState({ doc: null, project: null });
    Promise.all([get<{ project: ProjectSummary; doc: ProjectDoc }>(`/api/projects/${id}`), useLibrary.getState().fonts.length ? null : loadFonts()])
      .then(([r]) => {
        if (cancelled) return;
        loadProject(r.project, r.doc);
        const rec = readRecovery(id);
        if (rec && JSON.stringify(rec.doc) !== JSON.stringify(r.doc)) setRecovery(rec);
        else clearRecovery(id);
      })
      .catch((e) => setError(errorMessage(e)));
    return () => { cancelled = true; };
  }, [id, loadFonts]);

  useEffect(() => {
    const onBefore = (e: BeforeUnloadEvent) => {
      const s = useEditor.getState().saveState;
      if (s === 'unsaved' || s === 'saving' || s === 'failed' || s === 'conflict') { void flushPendingSave(); e.preventDefault(); }
    };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, []);

  if (error) return <div className="page"><div className="notice err">Could not open the project: {error}</div><button className="btn" style={{ marginTop: 12 }} onClick={() => navigate('/')}>Back to projects</button></div>;
  if (!doc || !project) return <div className="page"><Spinner label="Opening project…" /></div>;
  return (
    <>
      <EditorShell doc={doc} />
      {recovery && (
        <Modal title="Recover unsaved changes?" onClose={() => { clearRecovery(id); setRecovery(null); }}
          footer={<>
            <button className="btn" onClick={() => { clearRecovery(id); setRecovery(null); }}>Discard them</button>
            <button className="btn primary" onClick={() => { edit(() => recovery.doc, 'recovery'); setRecovery(null); toast('Unsaved changes recovered. They will be saved now.', 'ok'); }}>Recover changes</button>
          </>}>
          <p>This browser has changes to this project from {new Date(recovery.at).toLocaleString()} that were not saved to the server (for example because the tab closed or the connection dropped).</p>
          {recovery.baseVersion !== project.version && <div className="notice warn">The project has been saved elsewhere since (revision {recovery.baseVersion} → {project.version}). Recovering will replace those newer changes with this browser's version.</div>}
        </Modal>
      )}
    </>
  );
}

function SaveIndicator() {
  const state = useEditor((s) => s.saveState);
  const at = useEditor((s) => s.lastSavedAt);
  const err = useEditor((s) => s.saveError);
  const text = { saved: `Saved${at ? ' ' + new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}`, unsaved: 'Unsaved changes', saving: 'Saving…', failed: 'Save failed — retrying', conflict: 'Changed elsewhere' }[state];
  return (
    <div className={`save-status ${state}`} role="status" aria-live="polite" title={err ?? undefined}>
      {state === 'saving' ? <span className="spinner" aria-hidden /> : <span aria-hidden>{state === 'saved' ? '✓' : state === 'unsaved' ? '●' : '⚠'}</span>}
      {text}
    </div>
  );
}

function EditorShell({ doc }: { doc: ProjectDoc }) {
  const project = useEditor((s) => s.project)!;
  const stage = useEditor((s) => s.stage);
  const page = useEditor((s) => s.page);
  const selected = useEditor((s) => s.selected);
  const conflict = useEditor((s) => s.conflict);
  const canUndo = useEditor((s) => s.past.length > 0);
  const canRedo = useEditor((s) => s.future.length > 0);
  const res = useDocResources(doc);
  const all = useAllLayouts(doc, res);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showInspector, setShowInspector] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); void save('manual').then((ok) => ok && toast('Saved', 'ok', 1500)); return; }
      if (typing) return;
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); redo(); return; }
      const s = useEditor.getState();
      const pages = allPages(s.doc!).map((p) => p.key);
      const i = pages.indexOf(s.page);
      if (e.key === 'PageDown' || e.key === ']') { e.preventDefault(); useEditor.setState({ page: pages[Math.min(pages.length - 1, i + 1)] }); }
      else if (e.key === 'PageUp' || e.key === '[') { e.preventDefault(); useEditor.setState({ page: pages[Math.max(0, i - 1)] }); }
      else if (e.key === 'Escape') useEditor.setState({ selected: null });
      else if (s.selected && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        const id = s.selected;
        import('./store').then(({ edit }) => edit((d) => removeBlock(d, pageInfo(d, s.page).design, id), `del:${id}`));
        useEditor.setState({ selected: null });
      }
      else if (e.key === '0') useEditor.setState({ zoom: 'fit' });
      else if (e.key === '?') setShowShortcuts(true);
      else if (e.key === 'o') useEditor.setState({ view: s.view === 'single' ? 'overview' : 'single' });
      else if (s.selected && e.key.startsWith('Arrow')) {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 0.5;
        const el = all.layouts.get(s.page)?.boxes.find((b) => b.id === s.selected);
        if (!el) return;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        import('./store').then(({ setProp }) => setProp(s.selected!, ['rect'], { ...el.rect, x: +(el.rect.x + dx).toFixed(2), y: +(el.rect.y + dy).toFixed(2) }, `nudge:${s.selected}`));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [all.layouts]);

  const info = pageInfo(doc, page);
  const errors = all.issues.filter((i) => i.severity === 'error').length;
  return (
    <div className={`editor ${showInspector ? 'show-right' : ''}`}>
      <header className="ed-top">
        <button className="btn sm" onClick={async () => { await flushPendingSave(); navigate('/'); }} aria-label="Back to projects">← Projects</button>
        <div className="col ed-title" style={{ gap: 0, minWidth: 0 }}>
          <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>{project.name}</strong>
          <span className="small" style={{ color: '#aab4c0' }}>{project.customerName ?? 'No customer'} · {doc.year} · rev {project.version}</span>
        </div>
        <nav className="stages" aria-label="Workflow stages">
          {STAGES.map((s) => (
            <button key={s.n} aria-current={stage === s.n ? 'step' : undefined} onClick={() => useEditor.setState({ stage: s.n })}>
              <span className="num">{s.n}</span><span className="txt">{s.label}</span>
              {s.n === 4 && errors > 0 && <span className="badge err" style={{ padding: '0 6px' }} title="Blocking issues to fix before production files">{errors} {errors === 1 ? 'issue' : 'issues'}</span>}
            </button>
          ))}
        </nav>
        <SaveIndicator />
        <button className="btn icon sm" onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)" aria-label="Undo">↶</button>
        <button className="btn icon sm" onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo">↷</button>
        <button className="btn sm primary" onClick={() => save('manual').then((ok) => ok && toast('Saved', 'ok', 1500))} title="Save (Ctrl+S)">Save</button>
        <button className="btn icon sm" onClick={() => setShowShortcuts(true)} aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)">?</button>
      </header>

      {conflict && (
        <div style={{ gridColumn: '1 / -1' }} className="notice err row wrap" role="alert">
          <span className="grow"><strong>This project was saved elsewhere</strong> (revision {conflict.currentVersion} by {conflict.updatedBy}, {new Date(conflict.updatedAt).toLocaleTimeString()}). Your changes here are kept but not saved.</span>
          <button className="btn sm" onClick={() => location.reload()}>Reload their version</button>
          <button className="btn sm" onClick={async () => {
            const { post } = await import('../api');
            const s = useEditor.getState();
            try {
              const r = await post<{ id: string }>(`/api/projects/${s.project!.id}/duplicate`, { name: `${s.project!.name} (my copy)` });
              const { put } = await import('../api');
              await put(`/api/projects/${r.id}`, { baseVersion: 1, doc: s.doc, kind: 'manual' });
              clearRecovery(s.project!.id);
              toast('Your version was saved as a copy.', 'ok');
              navigate(`/projects/${r.id}`);
            } catch (e) { toast(errorMessage(e), 'err'); }
          }}>Save mine as a copy</button>
          <button className="btn sm danger" onClick={() => overwriteAfterConflict()}>Overwrite with mine</button>
        </div>
      )}

      <aside className="ed-left" aria-label={`${STAGES[stage - 1].label} settings`}>
        {stage === 1 && <ProductStage doc={doc} />}
        {stage === 2 && <DesignStage doc={doc} />}
        {stage === 3 && <ContentStage doc={doc} />}
        {stage === 4 && <ReviewStage doc={doc} issues={all.issues} layouts={all.layouts} />}
      </aside>

      <main className="ed-center">
        <Canvas doc={doc} res={res} layouts={all.layouts} onToggleInspector={() => setShowInspector(!showInspector)} />
      </main>

      <aside className="ed-right" aria-label="Selected element">
        <Inspector doc={doc} selected={selected} pageLabel={info.label} />
      </aside>

      <Strip doc={doc} layouts={all.layouts} issues={all.issues} res={res} overrides={(k) => hasOverride(doc, k)} />

      {showShortcuts && (
        <Modal title="Keyboard shortcuts" onClose={() => setShowShortcuts(false)}>
          <table className="tbl"><tbody>
            {[['Ctrl/⌘ + S', 'Save now (manual revision)'], ['Ctrl/⌘ + Z', 'Undo'], ['Ctrl/⌘ + Shift + Z, Ctrl + Y', 'Redo'], ['[  /  ]  or PageUp / PageDown', 'Previous / next page'],
              ['Arrow keys', 'Nudge selected element 0.5 mm (Shift: 5 mm)'], ['Delete / Backspace', 'Delete the selected block'], ['Alt while dragging', 'Place freely (no grid snap)'], ['Esc', 'Deselect'], ['0', 'Fit page to window'], ['o', 'Switch between single page and all pages'], ['?', 'This help']].map(([k, v]) => (
              <tr key={k}><td><span className="kbd">{k}</span></td><td>{v}</td></tr>
            ))}
          </tbody></table>
        </Modal>
      )}
    </div>
  );
}
